import { ArrowLeft, Pencil, Star, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFeedbacksByDate, getAllFeedbacks, updateFeedback, deleteFeedback } from '../../../api/feedback.api';
import styles from '../../../assets/scss/pages/FeedbackPage.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/formatters';

function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDateTime(str) {
    if (!str) return '—';
    const d = new Date(str);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

function Stars({ count, size = 14 }) {
    return (
        <span className={styles.stars}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={size} fill={i <= count ? '#FFD700' : 'none'} color={i <= count ? '#FFD700' : '#DFE6E9'} />
            ))}
        </span>
    );
}

function StarSelector({ value, onChange }) {
    return (
        <div className={styles.starSelector}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={24} fill={i <= value ? '#FFD700' : 'none'} color={i <= value ? '#FFD700' : '#DFE6E9'}
                    onClick={() => onChange(i)} style={{ cursor: 'pointer' }} />
            ))}
        </div>
    );
}

export default function FeedbackReviews() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.FEEDBACK.VIEW);
    const canUpdate = hasPermission(PERMISSIONS.FEEDBACK.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.FEEDBACK.DELETE);

    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('date');
    const [selectedDate, setSelectedDate] = useState(fmtDate(new Date()));
    const [toastMsg, setToastMsg] = useState(null);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
    const PAGE_SIZE = 30;
    const fetchIdRef = useRef(0);

    const [editModal, setEditModal] = useState(null);
    const [editForm, setEditForm] = useState({ serviceRating: 5, spaceRating: 5, comment: '' });
    const [editError, setEditError] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchFeedbacks = useCallback(async (p = page) => {
        if (!canView) { setLoading(false); return; }
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            let res;
            if (viewMode === 'date') {
                res = await getFeedbacksByDate(selectedDate, { page: p, limit: PAGE_SIZE });
            } else {
                res = await getAllFeedbacks({ page: p, limit: PAGE_SIZE });
            }
            if (fetchId !== fetchIdRef.current) return;
            setFeedbacks(res.data?.data || []);
            const info = res.data?.infopages || {};
            setPagination({ total: info.total || 0, totalPages: info.totalPages || 1 });
        } catch {
            if (fetchId !== fetchIdRef.current) return;
            setFeedbacks([]);
        } finally {
            if (fetchId === fetchIdRef.current) setLoading(false);
        }
    }, [canView, viewMode, selectedDate, page]);

    useEffect(() => { fetchFeedbacks(page); }, [fetchFeedbacks, page]);

    const openEdit = (fb) => {
        setEditModal(fb);
        setEditForm({ serviceRating: fb.serviceRating || 5, spaceRating: fb.spaceRating || 5, comment: fb.comment || '' });
        setEditError('');
    };

    const handleEditSave = async () => {
        if (!editForm.comment.trim()) { setEditError('Vui lòng nhập bình luận'); return; }
        setEditSaving(true);
        try {
            await updateFeedback(editModal.id, {
                billId: editModal.billId,
                serviceRating: editForm.serviceRating,
                spaceRating: editForm.spaceRating,
                comment: editForm.comment.trim(),
            });
            setEditModal(null);
            showToast('success', 'Cập nhật đánh giá thành công');
            fetchFeedbacks();
        } catch (err) {
            setEditError(err.response?.data?.message || 'Cập nhật thất bại');
        } finally { setEditSaving(false); }
    };

    const handleDelete = async (fbId) => {
        if (!canDelete) return;
        if (!window.confirm('Xoá đánh giá này?')) return;
        try {
            await deleteFeedback(fbId);
            showToast('success', 'Xoá thành công');
            fetchFeedbacks();
        } catch { showToast('error', 'Xoá thất bại', 2500); }
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button className={styles.backBtn} onClick={() => navigate('/admin/feedback')}><ArrowLeft size={20} /></button>
                    <div>
                        <h1 className={styles.title} style={{ fontSize: 20, marginBottom: 2 }}>Đánh giá</h1>
                        <p className={styles.subtitle}>Phản hồi từ khách hàng về chất lượng dịch vụ</p>
                    </div>
                </div>
            </div>

            <div className={styles.filters}>
                <button className={`${styles.filterBtn}${viewMode === 'date' ? ` ${styles.filterBtnActive}` : ''}`}
                    onClick={() => { setViewMode('date'); setPage(1); }}>Theo ngày</button>
                <button className={`${styles.filterBtn}${viewMode === 'all' ? ` ${styles.filterBtnActive}` : ''}`}
                    onClick={() => { setViewMode('all'); setPage(1); }}>Tất cả</button>
                {viewMode === 'date' && (
                    <input type="date" className={styles.dateInput} value={selectedDate}
                        onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }} />
                )}
                <span className={styles.filterCount}>{pagination.total} đánh giá</span>
            </div>

            {loading ? (
                <div className={styles.loading}><div className={styles.spinner} /> Đang tải...</div>
            ) : feedbacks.length === 0 ? (
                <div className={styles.empty}>Chưa có đánh giá nào</div>
            ) : (
                <div className={styles.feedbackList}>
                    {feedbacks.map((fb) => (
                        <div key={fb.id} className={styles.feedbackCard}
                            style={{ cursor: 'pointer' }}
                            onClick={() => fb.billId && navigate(`/admin/bills/${fb.billId}`)}>
                            <div className={styles.feedbackHeader}>
                                <span className={styles.feedbackCustomer}>{fb.bill?.customer?.name || 'Ẩn danh'}</span>
                                <div className={styles.feedbackActions}>
                                    {canUpdate && (
                                        <button className={`${styles.feedbackBtn} ${styles.feedbackBtnEdit}`}
                                            onClick={(e) => { e.stopPropagation(); openEdit(fb); }}><Pencil /></button>
                                    )}
                                    {canDelete && (
                                        <button className={`${styles.feedbackBtn} ${styles.feedbackBtnDelete}`}
                                            onClick={(e) => { e.stopPropagation(); handleDelete(fb.id); }}><Trash2 /></button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.feedbackRatings}>
                                <div className={styles.ratingRow}>Dịch vụ: <Stars count={fb.serviceRating} /></div>
                                <div className={styles.ratingRow}>Không gian: <Stars count={fb.spaceRating} /></div>
                            </div>
                            {fb.comment && <div className={styles.feedbackComment}>{fb.comment}</div>}
                            <div className={styles.feedbackBill}>
                                <span>{formatCurrency(fb.bill?.total || 0)}</span>
                                <span>{fmtDateTime(fb.bill?.checkoutTime)}</span>
                                <span>{fmtDateTime(fb.createdAt)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {pagination.totalPages > 1 && (
                <div className={styles.pagination} style={{ marginTop: 16 }}>
                    <span className={styles.paginationInfo}>
                        {`${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, pagination.total)} / ${pagination.total}`}
                    </span>
                    <div className={styles.paginationControls}>
                        <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>{'‹'}</button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                            .filter((n) => n === 1 || n === pagination.totalPages || Math.abs(n - page) <= 2)
                            .reduce((acc, n, i, arr) => {
                                if (i > 0 && n - arr[i - 1] > 1) acc.push('dots-' + i);
                                acc.push(n);
                                return acc;
                            }, [])
                            .map((n) =>
                                typeof n === 'string' ? (
                                    <span key={n} className={styles.pageDots}>...</span>
                                ) : (
                                    <button key={n} className={`${styles.pageBtn}${n === page ? ` ${styles.pageBtnActive}` : ''}`}
                                        onClick={() => setPage(n)}>{n}</button>
                                ),
                            )}
                        <button className={styles.pageBtn} disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>{'›'}</button>
                    </div>
                </div>
            )}

            {editModal && (
                <div className={styles.modalOverlay} onClick={() => setEditModal(null)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Chỉnh sửa đánh giá</h3>
                            <button className={styles.modalClose} onClick={() => setEditModal(null)}><X size={18} /></button>
                        </div>
                        <div className={styles.modalBody}>
                            {editError && <div className={styles.formError}>{editError}</div>}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Đánh giá dịch vụ</label>
                                <StarSelector value={editForm.serviceRating} onChange={(v) => setEditForm((p) => ({ ...p, serviceRating: v }))} />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Đánh giá không gian</label>
                                <StarSelector value={editForm.spaceRating} onChange={(v) => setEditForm((p) => ({ ...p, spaceRating: v }))} />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Bình luận</label>
                                <textarea className={styles.formTextarea} value={editForm.comment}
                                    onChange={(e) => setEditForm((p) => ({ ...p, comment: e.target.value }))}
                                    placeholder="Nhập bình luận..." />
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.modalBtn} onClick={() => setEditModal(null)}>Đóng</button>
                            <button className={`${styles.modalBtn} ${styles.modalBtnPrimary}`} onClick={handleEditSave} disabled={editSaving}>
                                {editSaving ? 'Đang lưu...' : 'Lưu'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div className={styles.toastIcon} style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
